import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Block } from '../classes/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeIcon from '@mui/icons-material/Merge';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BlockCreationPopup from './BlockCreationPopup';
import './BlockTabs.css';
import { t } from '@lingui/core/macro';
import { CardGroup, CardId, buildBlockCardMap } from '../utils/cardGroups';

interface BlockTabsProps {
  blocks: Block[];
  selectedBlockIndex: number | null;
  onBlockSelect: (index: number | null) => void;
  onBlocksChange: (newBlocks: Block[]) => void | Promise<void>;
  maxTabsHeight?: number;
  tabsTopOffset?: number;
  isCardsMode?: boolean;
  cardGroups?: CardGroup[];
  onSplitCardAt?: (blockId: string) => void;
  onMergeCardWithPrevious?: (cardId: CardId) => void;
}

interface VisibleBlock {
  block: Block;
  blockIndex: number;
}

interface SortableBlockTabProps {
  visibleBlock: VisibleBlock;
  isSelected: boolean;
  isEditing: boolean;
  isHovered: boolean;
  isCardsMode: boolean;
  cardGroup?: CardGroup;
  editingName: string;
  canSplit: boolean;
  canMerge: boolean;
  onSelect: (blockIndex: number) => void;
  onMouseEnter: (blockIndex: number) => void;
  onMouseLeave: () => void;
  onRenameSubmit: (e: React.FormEvent) => void;
  onRenameCancel: () => void;
  onRenameKeyDown: (e: React.KeyboardEvent) => void;
  onEditNameChange: (value: string) => void;
  onEditClick: (e: React.MouseEvent, blockIndex: number) => void;
  onDeleteClick: (e: React.MouseEvent, blockIndex: number) => void;
  onSplitCardAt?: (blockId: string) => void;
  onMergeCardWithPrevious?: (cardId: CardId) => void;
}

const SortableBlockTab: React.FC<SortableBlockTabProps> = ({
  visibleBlock,
  isSelected,
  isEditing,
  isHovered,
  isCardsMode,
  cardGroup,
  editingName,
  canSplit,
  canMerge,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  onRenameSubmit,
  onRenameCancel,
  onRenameKeyDown,
  onEditNameChange,
  onEditClick,
  onDeleteClick,
  onSplitCardAt,
  onMergeCardWithPrevious,
}) => {
  const { block, blockIndex } = visibleBlock;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: isEditing,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isFirstInCard: boolean = isCardsMode && cardGroup !== undefined && cardGroup.blockIds[0] === block.id;

  return (
    <div
      ref={setNodeRef}
      data-block-index={blockIndex}
      className={`block-tab ${isSelected ? 'active' : ''}${isCardsMode ? ' cards-mode' : ''}${isDragging ? ' dragging' : ''}`}
      style={style}
      onClick={() => onSelect(blockIndex)}
      onMouseEnter={() => onMouseEnter(blockIndex)}
      onMouseLeave={onMouseLeave}
    >
      {isCardsMode && cardGroup && (
        <span className="card-accent-strip" style={{ background: cardGroup.color }} />
      )}
      <button
        type="button"
        className="block-drag-handle"
        ref={setActivatorNodeRef}
        title="Reorder block"
        aria-label="Reorder block"
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </button>
      {isEditing ? (
        <form onSubmit={onRenameSubmit} onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={editingName}
            onChange={(e) => onEditNameChange(e.target.value)}
            onBlur={onRenameCancel}
            onKeyDown={onRenameKeyDown}
            autoFocus
            className="block-rename-input"
          />
        </form>
      ) : (
        <div className="block-tab-content">
          <div className="block-name-row">
            {isFirstInCard && (
              <span className="card-badge" style={{ background: cardGroup?.color }}>
                {cardGroup?.name}
              </span>
            )}
            <span className="block-name" title={block.name}>{block.name}</span>
          </div>
          {isHovered && (
            <div className="block-actions">
              <button
                className="block-action-btn edit-btn"
                onClick={(e) => onEditClick(e, blockIndex)}
                title="Rename block"
              >
                <EditIcon fontSize="small" />
              </button>
              <button
                className="block-action-btn delete-btn"
                onClick={(e) => onDeleteClick(e, blockIndex)}
                title="Delete block"
              >
                <DeleteIcon fontSize="small" />
              </button>
              {canSplit && onSplitCardAt && (
                <button
                  className="block-action-btn split-card-btn"
                  onClick={(e) => { e.stopPropagation(); onSplitCardAt(block.id); }}
                  title="Start new card here"
                >
                  <CallSplitIcon fontSize="small" />
                </button>
              )}
              {canMerge && onMergeCardWithPrevious && cardGroup && (
                <button
                  className="block-action-btn merge-card-btn"
                  onClick={(e) => { e.stopPropagation(); onMergeCardWithPrevious(cardGroup.id); }}
                  title="Merge with previous card"
                >
                  <MergeIcon fontSize="small" />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BlockTabs: React.FC<BlockTabsProps> = ({
  blocks,
  selectedBlockIndex,
  onBlockSelect,
  onBlocksChange,
  maxTabsHeight,
  tabsTopOffset,
  isCardsMode = false,
  cardGroups = [],
  onSplitCardAt,
  onMergeCardWithPrevious,
}) => {
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
  const [showCreationPopup, setShowCreationPopup] = useState<boolean>(false);
  const tabsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedBlockIndex === null || !tabsListRef.current) return;
    const selectedTab: HTMLElement | null = tabsListRef.current.querySelector(
      `.block-tab[data-block-index="${selectedBlockIndex}"]`
    );
    selectedTab?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedBlockIndex]);

  const visibleBlocks = useMemo<VisibleBlock[]>(
    () => blocks
      .map((block, blockIndex) => ({ block, blockIndex }))
      .filter(({ block }) => block?.name?.trim() && block.name !== "undefined"),
    [blocks]
  );

  const visibleBlockIds = useMemo<UniqueIdentifier[]>(
    () => visibleBlocks.map(({ block }) => block.id),
    [visibleBlocks]
  );

  const handleBlockClick = useCallback((blockIndex: number | null) => {
    onBlockSelect(blockIndex);
  }, [onBlockSelect]);

  const handleEditClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block = blocks[blockIndex];
    if (block) {
      setEditingBlockIndex(blockIndex);
      setEditingName(block.name);
    }
  }, [blocks]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const newBlocks = blocks.filter((_, index) => index !== blockIndex);
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }

    if (selectedBlockIndex !== null) {
      if (blockIndex === selectedBlockIndex) {
        onBlockSelect(newBlocks.length > 0 ? 0 : null);
      } else if (blockIndex < selectedBlockIndex) {
        onBlockSelect(selectedBlockIndex - 1);
      }
    }
  }, [blocks, selectedBlockIndex, onBlocksChange, onBlockSelect]);

  const handleRenameSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (editingBlockIndex !== null && editingName.trim()) {
      const newBlocks = [...blocks];
      newBlocks[editingBlockIndex] = Block.create({
        ...newBlocks[editingBlockIndex],
        name: editingName.trim()
      });
      const result = onBlocksChange(newBlocks);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onBlocksChange:', error);
        });
      }
      setEditingBlockIndex(null);
      setEditingName('');
    }
  }, [editingBlockIndex, editingName, blocks, onBlocksChange]);

  const handleRenameCancel = useCallback(() => {
    setEditingBlockIndex(null);
    setEditingName('');
  }, []);

  const handleAddBlock = useCallback(() => {
    setShowCreationPopup(true);
  }, []);

  const handleCreateEmptyBlock = useCallback(() => {
    const newBlock = Block.create({
      name: `Block ${blocks.length + 1}`,
      blockNameColor: '#ffffff',
      labelFontColor: '#ffffff',
      valueFontColor: '#ffffff',
      dataRows: [{ col0: '', col1: 'New', col2: 'Block' }]
    });
    const newBlocks = [...blocks, newBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    setEditingBlockIndex(newBlockIndex);
    setEditingName(newBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  const handleCloneBlock = useCallback((sourceBlock: Block) => {
    const clonedBlock = Block.create({
      ...sourceBlock,
      id: undefined,
      name: `${sourceBlock.name} ` + t`(Copy)`,
      dataRows: sourceBlock.dataRows.map(row => ({ ...row }))
    });
    
    const newBlocks = [...blocks, clonedBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    setEditingBlockIndex(newBlockIndex);
    setEditingName(clonedBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  const getBlockIndexById = useCallback((id: UniqueIdentifier): number => {
    return blocks.findIndex((block: Block) => block.id === id);
  }, [blocks]);

  const handleDragEnd = useCallback((event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex: number = getBlockIndexById(active.id);
    const newIndex: number = getBlockIndexById(over.id);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const reorderedBlocks: Block[] = arrayMove(blocks, oldIndex, newIndex);
    const result: void | Promise<void> = onBlocksChange(reorderedBlocks);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error('Error in onBlocksChange:', error);
      });
    }

    if (selectedBlockIndex === null) {
      return;
    }

    if (selectedBlockIndex === oldIndex) {
      onBlockSelect(newIndex);
      return;
    }

    if (oldIndex < selectedBlockIndex && selectedBlockIndex <= newIndex) {
      onBlockSelect(selectedBlockIndex - 1);
      return;
    }

    if (newIndex <= selectedBlockIndex && selectedBlockIndex < oldIndex) {
      onBlockSelect(selectedBlockIndex + 1);
    }
  }, [blocks, getBlockIndexById, onBlockSelect, onBlocksChange, selectedBlockIndex]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleRenameCancel();
    }
  }, [handleRenameCancel]);

  const tabsStyle: React.CSSProperties = {
    ...(maxTabsHeight ? { maxHeight: `${maxTabsHeight}px` } : {}),
    ...(tabsTopOffset ? { marginTop: `${tabsTopOffset}px` } : {}),
  };

  const blockCardMap = useMemo(
    () => (isCardsMode ? buildBlockCardMap(cardGroups) : new Map()),
    [isCardsMode, cardGroups]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <div className="block-tabs-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleBlockIds} strategy={verticalListSortingStrategy}>
          <div className="block-tabs" style={tabsStyle} ref={tabsListRef}>
            {visibleBlocks.map((visibleBlock: VisibleBlock) => {
              const { block, blockIndex } = visibleBlock;
              const isSelected: boolean = selectedBlockIndex === blockIndex;
              const isEditing: boolean = editingBlockIndex === blockIndex;
              const isHovered: boolean = hoveredBlockIndex === blockIndex;
              const cardGroup: CardGroup | undefined = isCardsMode ? blockCardMap.get(block.id) : undefined;
              const isFirstInCard: boolean = isCardsMode && cardGroup !== undefined && cardGroup.blockIds[0] === block.id;
              const isFirstCard: boolean = isFirstInCard && cardGroups[0]?.id === cardGroup?.id;
              const canSplit: boolean = isCardsMode && cardGroup !== undefined && !isFirstInCard;
              const canMerge: boolean = isCardsMode && isFirstInCard && !isFirstCard;

              return (
                <SortableBlockTab
                  key={block.id}
                  visibleBlock={visibleBlock}
                  isSelected={isSelected}
                  isEditing={isEditing}
                  isHovered={isHovered}
                  isCardsMode={isCardsMode}
                  cardGroup={cardGroup}
                  editingName={editingName}
                  canSplit={canSplit}
                  canMerge={canMerge}
                  onSelect={handleBlockClick}
                  onMouseEnter={setHoveredBlockIndex}
                  onMouseLeave={() => setHoveredBlockIndex(null)}
                  onRenameSubmit={handleRenameSubmit}
                  onRenameCancel={handleRenameCancel}
                  onRenameKeyDown={handleKeyDown}
                  onEditNameChange={setEditingName}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                  onSplitCardAt={onSplitCardAt}
                  onMergeCardWithPrevious={onMergeCardWithPrevious}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      <button
        className="add-block-btn"
        onClick={handleAddBlock}
        title="Add new block"
      >
        <AddIcon fontSize="small" />
      </button>

      <BlockCreationPopup
        isOpen={showCreationPopup}
        onClose={() => setShowCreationPopup(false)}
        onCreateEmpty={handleCreateEmptyBlock}
        onCloneBlock={handleCloneBlock}
        existingBlocks={visibleBlocks.map(({ block }) => block)}
      />
    </div>
  );
};

export default BlockTabs; 